/** Small pure helpers shared by the paycheck, income-tax, VAT, GST and SIP calculators. */
import { parseNumber } from "./numberInput";
import { K401_LIMIT, K401_CATCHUP_50, K401_CATCHUP_60_63 } from "./k401";

/** Error text for a required money field: blank, non-numeric or negative. Empty string when valid. */
export function amountError(raw: string, label: string): string {
  const n = parseNumber(raw);
  if (n == null) return `Enter ${label} as a number.`;
  if (n < 0) return `Enter ${label} of 0 or more. It cannot be negative.`;
  return "";
}

/** Age options for the 401(k) limit. The ages stand for the band (IRS Notice 2025-67 catch-up rules for 2026). */
export type K401Age = "under50" | "50plus" | "60to63";
export const K401_AGE_LABEL: Record<K401Age, string> = { under50: "Under 50", "50plus": "50 or older", "60to63": "Age 60 to 63" };
export function k401LimitFor(age: K401Age): number {
  return age === "60to63" ? K401_LIMIT + K401_CATCHUP_60_63 : age === "50plus" ? K401_LIMIT + K401_CATCHUP_50 : K401_LIMIT;
}

/** Error text for annual pre-tax contributions against income and the 2026 employee 401(k) limit for the chosen age band. */
export function pretaxError(raw: string, income: number, applyLimit: boolean, age: K401Age = "under50"): string {
  const n = parseNumber(raw);
  if (raw.trim() === "") return "";
  if (n == null) return "Enter pre-tax contributions as a number.";
  if (n < 0) return "Pre-tax contributions cannot be negative.";
  if (n > income) return "Pre-tax contributions cannot be larger than your income.";
  const limit = k401LimitFor(age);
  if (applyLimit && n > limit) {
    const who = age === "under50" ? "employee 401(k) limit" : age === "50plus" ? "401(k) limit with the age 50 catch-up" : "401(k) limit with the age 60 to 63 catch-up";
    const hint = age === "under50" ? " If you are 50 or older, choose your age group to include the catch-up." : "";
    return `Pre-tax contributions are above the 2026 ${who} of $${limit.toLocaleString("en-US")} (IRS Notice 2025-67). Enter ${limit.toLocaleString("en-US")} or less.${hint}`;
  }
  return "";
}

export const QUEBEC_GST_PCT = 5;
export const QUEBEC_QST_PCT = 9.975;
const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Quebec GST (5%) and QST (9.975%) are each charged on the net price; show them separately. */
export function quebecSplit(net: number): { gst: number; qst: number } {
  return { gst: r2((net * QUEBEC_GST_PCT) / 100), qst: r2((net * QUEBEC_QST_PCT) / 100) };
}

/** Effective annual rate (%) when the stated annual rate is compounded monthly at rate/12. */
export function effectiveAnnualPct(ratePct: number): number {
  return (Math.pow(1 + ratePct / 100 / 12, 12) - 1) * 100;
}

/** Short English summary such as "1.16 million"; empty below one million. */
export function millionsSummary(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e12) return `${(n / 1e12).toFixed(2)} trillion`;
  if (a >= 1e9) return `${(n / 1e9).toFixed(2)} billion`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(2)} million`;
  return "";
}
